import { Box, Snackbar, type SnackbarOrigin } from '@mui/material'
import MuiAlert, { type AlertProps } from '@mui/material/Alert'
import { createContext, useContext, useId, useMemo, useReducer } from 'react'

const Context = createContext<{
	showMessage: (
		message: any,
		options?: {
			action?: string
			severity?: AlertProps['severity']
		},
	) => void
}>({
	showMessage: () => {
		return
	},
})

function Alert(props: AlertProps) {
	return <MuiAlert elevation={6} variant="filled" {...props} />
}

export const useSnackbar = () => useContext(Context)

type SnackbarProps = {
	showMessage: (
		message: any,
		options?: {
			action?: string
			severity?: AlertProps['severity']
		},
	) => void
}

export const withSnackbar =
	<P extends object>(
		Target: React.ComponentType<P & SnackbarProps>,
	): React.FC<P> =>
	(props: P) => {
		const values = useSnackbar()

		return <Target {...props} {...values} />
	}

const initialValue = { open: false, message: '', action: null }

const ACTIONS = {
	OPEN: 'open',
	CLOSE: 'close',
}

function reducer(state, action) {
	switch (action.type) {
		case ACTIONS.OPEN:
			return { ...state, ...action.payload, open: true }

		case ACTIONS.CLOSE:
			return { ...state, open: false }

		default:
			throw Error(`invalid action type '${action.type}'`)
	}
}

export function Provider({
	horizontal,
	...props
}: React.PropsWithChildren<{ horizontal?: SnackbarOrigin['horizontal'] }>) {
	const messageId = useId()
	const [state, dispatch] = useReducer(reducer, initialValue)

	const value = useMemo(() => {
		function open(
			message: any,
			options: { action?: string; severity?: AlertProps['severity'] } = {
				action: undefined,
				severity: undefined,
			},
		) {
			dispatch({ type: ACTIONS.OPEN, payload: { message, ...options } })
		}

		function close() {
			dispatch({ type: ACTIONS.CLOSE })
		}

		return {
			showMessage: open,
			closeMessage: close,
		}
	}, [])

	return (
		<>
			<Context.Provider {...props} value={value} />

			<Snackbar
				open={state.open && Boolean(state.severity)}
				autoHideDuration={6000}
				onClose={value.closeMessage}
				sx={horizontal ? undefined : { marginLeft: 7, zIndex: 99000 }}
				anchorOrigin={{ vertical: 'bottom', horizontal: horizontal ?? 'left' }}
			>
				<Box>
					<Alert
						onClose={value.closeMessage}
						severity={state.severity}
						action={state.action}
					>
						{state.message}
					</Alert>
				</Box>
			</Snackbar>

			<Snackbar
				open={state.open && !state.severity}
				autoHideDuration={6000}
				onClose={value.closeMessage}
				slotProps={{
					content: {
						'aria-describedby': messageId,
					},
				}}
				message={<span id={messageId}>{state.message}</span>}
				action={state.action}
				sx={{ marginLeft: 7 }}
			/>
		</>
	)
}
